/**
 * ChildRequirementsTable — requirements inherited from child TARAs.
 * Read-only data + Relevant checkbox + Justification input.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { getCALColor } from '../../../../utils/severityStyle';
import { CAL_LEVELS, REQUIREMENT_TYPES, type RequirementRecord } from './requirementsFieldSchema';
import styles from './Step8Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  REQUIREMENT_TYPES.map((t) => [t.value, t.label]),
);

const renderType = (row: ChildRequirementView) =>
  row.requirement_type ? (
    <CategoryPill label={TYPE_LABELS[row.requirement_type] ?? String(row.requirement_type)} size="sm" />
  ) : '';

const renderCal = (row: ChildRequirementView) =>
  row.cal_level ? (
    <LevelBadge color={getCALColor(row.cal_level)} label={String(row.cal_level)} size="sm" />
  ) : '';

export interface ChildRequirementView extends RequirementRecord {}

export interface ChildRequirementsTableProps {
  requirements: ChildRequirementView[];
  onToggleRelevance: (reqId: string) => void;
  onJustificationChange: (reqId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildRequirementsTable({
  requirements,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildRequirementsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [calValues, setCalValues] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const calCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of CAL_LEVELS) counts[c.value] = 0;
    for (const r of requirements) {
      const k = String(r.cal_level ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [requirements]);

  const calFiltered = useMemo(
    () => (calValues.length === 0
      ? requirements
      : requirements.filter((r) => calValues.includes(String(r.cal_level ?? '')))),
    [requirements, calValues],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return calFiltered;
    return calFiltered.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [calFiltered, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );
  const keyed = useMemo(
    () => paged.map((r, i) => ({ ...r, _rowKey: `${r.req_id ?? 'row'}::${(page - 1) * pageSize + i}` })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'req_id', label: 'ID', sortable: true, width: '6%' },
      { key: 'req_title', label: 'Title', sortable: true, width: '14%' },
      { key: 'req_statement', label: 'Statement', sortable: false, width: '24%' },
      { key: 'req_rationale', label: 'Rationale', sortable: false, width: '18%' },
      { key: 'requirement_type', label: 'Type', sortable: true, width: '9%', render: renderType },
      { key: 'control_id', label: 'Control', sortable: true, width: '6%' },
      { key: 'cal_level', label: 'CAL', sortable: true, width: '5%', render: renderCal },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '8%',
        render: (row: ChildRequirementView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.req_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.req_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '10%',
        render: (row: ChildRequirementView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.req_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  const calFilter = {
    key: 'cal_level',
    label: 'CAL',
    values: calValues,
    onChange: (next: string[]) => { setCalValues(next); setPage(1); },
    options: CAL_LEVELS.map((c) => ({ value: c.value, label: c.label, count: calCounts[c.value] ?? 0 })),
  };

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[calFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        emptyMessage="No inherited requirements from child TARAs."
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search inherited requirements…',
            }}
            count={{ value: filtered.length, label: 'Inherited Requirements' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || calValues.length > 0}
            actions={
              <RelevanceEditToggle
                editing={liveEditing}
                onToggle={handleToggle}
                pending={saving}
                disabled={disabled}
              />
            }
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(1); }}
          />
        }
      />
    </div>
  );
}
